import { create } from 'zustand';
import type { InterviewMessage } from '../types/interview';

type InterviewStatus =
  | 'idle'
  | 'loading'
  | 'consent'
  | 'chatting'
  | 'completing'
  | 'completed'
  | 'error';

interface InterviewState {
  surveyId: string | null;
  token: string | null;
  status: InterviewStatus;
  messages: InterviewMessage[];
  currentQuestion: number;
  totalQuestions: number;
  consentGiven: boolean;
  error: string | null;

  setToken: (token: string) => void;
  setSurveyId: (id: string) => void;
  setStatus: (status: InterviewStatus) => void;
  addMessage: (msg: Omit<InterviewMessage, 'id' | 'timestamp'>) => void;
  updateFirstAiMessage: (content: string) => void;
  setConsent: (consent: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  surveyId: null,
  token: null,
  status: 'idle' as InterviewStatus,
  messages: [],
  currentQuestion: 0,
  totalQuestions: 0,
  consentGiven: false,
  error: null,
};

export const useInterviewStore = create<InterviewState>((set) => ({
  ...initialState,

  setToken: (token) => set({ token }),

  setSurveyId: (id) => set({ surveyId: id }),

  setStatus: (status) => set({ status }),

  addMessage: (msg) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          ...msg,
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
        },
      ],
    })),

  updateFirstAiMessage: (content) =>
    set((state) => {
      const idx = state.messages.findIndex((m) => m.role === 'ai');
      if (idx === -1) return state;
      const updated = [...state.messages];
      updated[idx] = { ...updated[idx], content };
      return { messages: updated };
    }),

  setConsent: (consent) => set({ consentGiven: consent }),

  setError: (error) => set({ error, status: error ? 'error' : 'idle' }),

  reset: () => set(initialState),
}));
